import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ClockCountdown,
  FileArrowDown,
  FolderOpen,
  ShieldCheck,
  Users,
  ArrowRight,
  Check,
  VideoCamera,
  Hammer,
  Wrench,
  Lightning,
  Calculator,
} from "@phosphor-icons/react";
import DemoBookingModal from "@/components/DemoBookingModal";

// Priset speglar backend/scripts/setup_stripe_products.py — samma volume-tiered
// bucket-modell som /pricing men förenklat till 3 kort utan räknare.
// OBS: monthly/yearly här visas INKLUSIVE 25 % svensk moms (samma som /pricing).
const TIER_CARDS = [
  { label: "1–5 platser", yearly: 9000, monthly: 750, features: ["Alla funktioner", "Årsfakturering", "30 dagar gratis"] },
  {
    label: "6–10 platser",
    yearly: 13500,
    monthly: 1125,
    features: ["Alla funktioner", "Årsfakturering", "30 dagar gratis"],
    popular: true,
  },
  {
    label: "11+ platser",
    yearly: 18000,
    monthly: 1500,
    features: ["Alla funktioner", "Obegränsat antal platser", "30 dagar gratis"],
  },
];

const PAINS = [
  {
    icon: ClockCountdown,
    title: "Timmar som försvinner",
    body:
      "En glömd timdebitering per anställd och månad kan vara flera tusen kronor bort. Excel-mallar och whiteboardtavlor läcker tid — Tidsrapporten fångar varje minut vid källan.",
  },
  {
    icon: FileArrowDown,
    title: "Rapporter som tar halva fredagen",
    body:
      "Slut på att klistra ihop CSV, sortera manuellt och maila kunden en Excel som ingen kan öppna. Tidsrapporten exporterar PDF med rätt logga och delsummor på ett klick.",
  },
  {
    icon: FolderOpen,
    title: "Filer utspridda överallt",
    body:
      "Ritningar i Dropbox, kalkyler i mailen, DXF på en pinne i verktygslådan. Tidsrapporten samlar PDF, Excel och AutoCAD-filer per objekt — och zippar hela mappen när jobbet är klart.",
  },
];

const FEATURES = [
  "Stämpelklocka med objekt och moment",
  "Automatisk PDF- och CSV-export",
  "Filuppladdning per objekt (PDF, Excel, DXF)",
  "Zip-arkivering av avslutade objekt",
  "Admin-redigering med spårbarhet",
  "Bidirektionellt API för egna integrationer",
  "Grupperingar och delsummor per moment",
  "Egen logotyp, färg och företagsnamn",
  "Bjud in obegränsat antal kollegor",
];

// Branschexempel som visar bredden — inga påhittade kundnamn, bara
// verksamhetstyper som passar Tidsrapporten. Håll formuleringar konkreta
// så besökaren känner igen sitt eget flöde.
const INDUSTRIES = [
  {
    icon: Hammer,
    title: "Snickeri & montörer",
    tagline: "Uppmätning → montage → slutbesiktning",
    body:
      "Följ varje moment på ett objekt, ladda upp ritningar och CAD-filer, zippa hela mappen när jobbet är slutbesiktat.",
    href: "/tidrapportering-snickeri",
  },
  {
    icon: Wrench,
    title: "Bygg & renovering",
    tagline: "Personalliggare & tidrapport i ett",
    body:
      "Elektronisk personalliggare enligt Skatteverkets krav. Stämpla in på rätt hus, gruppera timmar per etapp och exportera direkt till beställaren.",
    href: "/tidrapportering-bygg",
  },
  {
    icon: Lightning,
    title: "El & installation",
    tagline: "Serviceavtal + nyinstallation i ett flöde",
    body:
      "Blixtsnabb start på jobbet, byte mellan objekt utan att förlora tid, spårbara ändringar för certifieringen.",
  },
  {
    icon: Calculator,
    title: "Redovisning & konsult",
    tagline: "Debiterbar tid utan Excel-cirkus",
    body:
      "Rapportera per klientuppdrag, dela underlag med kollegor och skicka faktureringsklara sammanställningar månadsvis.",
  },
];

function fmtSEK(n) {
  return n.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
}

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-app text-text-primary">
      {/* NAV */}
      <header
        className={
          "sticky top-0 z-40 transition-all " +
          (scrolled
            ? "bg-app/90 backdrop-blur border-b border-app"
            : "bg-transparent")
        }
      >
        <nav className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="landing-nav-brand"
          >
            <span className="h-8 w-8 rounded-md bg-sidebar flex items-center justify-center text-sidebar-text font-heading text-sm font-bold">
              T
            </span>
            <span className="font-heading text-lg tracking-tight">
              Tidsrapporten
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a
              href="#varfor"
              className="hover:text-brand transition-colors"
              data-testid="landing-nav-why"
            >
              Varför Tidsrapporten
            </a>
            <a
              href="#funktioner"
              className="hover:text-brand transition-colors"
              data-testid="landing-nav-features"
            >
              Funktioner
            </a>
            <Link
              to="/pricing"
              className="hover:text-brand transition-colors"
              data-testid="landing-nav-pricing"
            >
              Priser
            </Link>
            <Link
              to="/integrationer"
              className="hover:text-brand transition-colors"
              data-testid="landing-nav-integrations"
            >
              Integrationer
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm hover:text-brand transition-colors"
              data-testid="landing-nav-login"
            >
              Logga in
            </Link>
            <button
              onClick={() => navigate("/register")}
              className="text-sm bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md transition-colors"
              data-testid="landing-nav-cta"
            >
              Prova gratis
            </button>
          </div>
        </nav>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <span
              className="inline-block text-[11px] tracking-widest uppercase text-text-muted mb-6"
              data-testid="landing-hero-eyebrow"
            >
              Tidrapportering för projektbaserade team
            </span>
            <h1
              className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]"
              data-testid="landing-hero-title"
            >
              Slut på borttappade
              <br />
              <span className="text-brand">arbetstimmar.</span>
            </h1>
            <p
              className="mt-6 text-lg text-text-muted max-w-lg leading-relaxed"
              data-testid="landing-hero-subtitle"
            >
              Tidsrapporten är stämpelklockan, PDF-rapporten och filarkivet i en
              enda app — byggd för konsulter, hantverkare, tekniker och alla
              andra som fakturerar tid mot projekt.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/register")}
                className="bg-brand hover:bg-brand-hover text-white px-6 py-3.5 rounded-md font-medium flex items-center gap-2 transition-colors"
                data-testid="landing-hero-cta-primary"
              >
                Starta gratis prövotid
                <ArrowRight size={18} weight="bold" />
              </button>
              <button
                onClick={() => setDemoOpen(true)}
                className="group relative bg-sidebar text-sidebar-text hover:bg-forest px-6 py-3.5 rounded-md font-medium flex items-center gap-2 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02]"
                data-testid="landing-hero-cta-demo"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-status-active opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-status-active" />
                </span>
                <VideoCamera size={18} weight="fill" />
                Boka live demo
              </button>
              <Link
                to="/pricing"
                className="border border-app hover:bg-surface-hover px-6 py-3.5 rounded-md font-medium transition-colors"
                data-testid="landing-hero-cta-secondary"
              >
                Se priser
              </Link>
            </div>
            <p className="mt-4 text-xs text-text-muted">
              30 dagar gratis · Inget kort krävs · Avsluta när du vill
            </p>
          </div>

          {/* Hero visual — mock av stämpelklockan */}
          <div className="relative" data-testid="landing-hero-visual">
            <div className="absolute -inset-6 bg-brand/10 rounded-3xl blur-2xl" />
            <div className="relative bg-sidebar rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <span className="text-[11px] tracking-widest uppercase text-sidebar-text">
                  Stämpelklocka
                </span>
                <span className="text-xs text-status-active flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-status-active animate-pulse" />
                  Stämplad in
                </span>
              </div>
              <div className="text-sidebar-text text-sm mb-2 opacity-80">
                Anders Karlsson
              </div>
              <div className="text-sidebar-text font-heading text-5xl tracking-tight tabular-nums">
                02:47:13
              </div>
              <div className="mt-8 space-y-3">
                <div className="bg-sidebar-active rounded-md p-3 text-sm text-sidebar-text flex justify-between">
                  <span>Projekt #142 — Kundleverans</span>
                  <span className="opacity-60">Objekt</span>
                </div>
                <div className="bg-sidebar-active rounded-md p-3 text-sm text-sidebar-text flex justify-between">
                  <span>Konsultation</span>
                  <span className="opacity-60">Moment</span>
                </div>
              </div>
              <button
                className="mt-6 w-full bg-brand hover:bg-brand-hover text-white py-3 rounded-md font-medium transition-colors"
                type="button"
                disabled
                aria-hidden
              >
                Stämpla ut
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM / PAINS */}
      <section
        id="varfor"
        className="border-t border-app bg-surface"
        data-testid="landing-pains"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl mb-14">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Varför team byter
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Tre problem som kostar mest —
              <br />
              lösta med samma app.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {PAINS.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="bg-app border border-app rounded-xl p-7"
                data-testid={`landing-pain-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-brand/10 flex items-center justify-center text-brand mb-5">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-heading text-xl mb-3">{title}</h3>
                <p className="text-sm text-text-muted leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALLOUT */}
      <section
        className="bg-sidebar text-sidebar-text"
        data-testid="landing-roi"
      >
        <div className="mx-auto max-w-6xl px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[11px] tracking-widest uppercase text-sidebar-text/60">
              Räkna hem investeringen
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              En återfångad timme
              <br />
              <span className="text-brand">= appen på köpet.</span>
            </h2>
          </div>
          <div className="space-y-4">
            <p className="text-lg text-sidebar-text/90 leading-relaxed">
              Om en anställd tappar bara <strong>en timme i månaden</strong>{" "}
              i orapporterad tid — säg 850 kr timdebitering — kostar det er{" "}
              <strong>10 200 kr per person och år</strong>.
            </p>
            <p className="text-lg text-sidebar-text/90 leading-relaxed">
              Tidsrapporten börjar på <strong>750 kr/månad</strong> (inkl. moms) för hela
              teamet upp till 5 personer.{" "}
              <span className="text-brand">Ni räknar hem den nästan direkt.</span>
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-brand hover:text-white transition-colors mt-4"
              data-testid="landing-roi-cta"
            >
              Räkna ut för ert team
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="funktioner"
        className="border-t border-app"
        data-testid="landing-features"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl mb-14">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Allt du behöver
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Ingen tilläggsavgift.
              <br />
              Inga integrationer att köpa.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f}
                className="flex items-start gap-3 py-3"
                data-testid={`landing-feature-${i}`}
              >
                <span className="h-6 w-6 rounded-full bg-brand/10 text-brand flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} weight="bold" />
                </span>
                <span className="text-sm leading-relaxed">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section
        id="branscher"
        className="bg-surface border-t border-app"
        data-testid="landing-industries"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl mb-14">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Passar för fler än trappmakare
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Byggd för hantverket.
              <br />
              <span className="text-brand">Öppen för alla branscher.</span>
            </h2>
            <p className="mt-5 text-lg text-text-muted leading-relaxed">
              Objekt, moment och tidrapporter är generella nog för att fungera
              lika bra i en snickerifirma som på ett redovisningskontor. Här är
              några typexempel:
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ind.icon;
              const CardInner = (
                <>
                  <div className="h-11 w-11 rounded-lg bg-brand/10 text-brand flex items-center justify-center mb-4">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="font-heading text-xl leading-tight mb-1">
                    {ind.title}
                  </h3>
                  <div className="text-[11px] tracking-widest uppercase text-brand/80 mb-3">
                    {ind.tagline}
                  </div>
                  <p className="text-sm text-text-muted leading-relaxed">
                    {ind.body}
                  </p>
                  {ind.href && (
                    <div className="mt-4 text-sm font-medium text-brand flex items-center gap-1.5">
                      Läs mer <ArrowRight size={14} weight="bold" />
                    </div>
                  )}
                </>
              );

              const commonClass = `bg-app border border-app rounded-xl p-6 transition-colors ${
                ind.href
                  ? "hover:border-brand hover:shadow-md cursor-pointer block"
                  : "hover:border-brand"
              }`;

              if (ind.href) {
                return (
                  <Link
                    key={ind.title}
                    to={ind.href}
                    className={commonClass}
                    data-testid={`landing-industry-${i}`}
                  >
                    {CardInner}
                  </Link>
                );
              }

              return (
                <div
                  key={ind.title}
                  className={commonClass}
                  data-testid={`landing-industry-${i}`}
                >
                  {CardInner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section
        className="bg-surface border-y border-app"
        data-testid="landing-trust"
      >
        <div className="mx-auto max-w-6xl px-6 py-14 grid md:grid-cols-3 gap-8 text-center md:text-left">
          <div className="flex items-start gap-4 justify-center md:justify-start">
            <ShieldCheck size={26} weight="duotone" className="text-brand flex-shrink-0 mt-1" />
            <div>
              <div className="font-heading text-lg">Byggd i Sverige</div>
              <p className="text-sm text-text-muted mt-1">
                Svensk moms, svensk support, svenska affärsflöden.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 justify-center md:justify-start">
            <Users size={26} weight="duotone" className="text-brand flex-shrink-0 mt-1" />
            <div>
              <div className="font-heading text-lg">Multi-tenant SaaS</div>
              <p className="text-sm text-text-muted mt-1">
                Egen inloggning, eget företagsnamn, egen logga per organisation.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 justify-center md:justify-start">
            <ClockCountdown size={26} weight="duotone" className="text-brand flex-shrink-0 mt-1" />
            <div>
              <div className="font-heading text-lg">30 dagar gratis</div>
              <p className="text-sm text-text-muted mt-1">
                Inget kort vid registrering. Avsluta innan prövotiden — inga
                pengar går ut.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section
        className="border-t border-app"
        data-testid="landing-pricing-teaser"
      >
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[11px] tracking-widest uppercase text-text-muted">
              Priser
            </span>
            <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight leading-tight">
              Ett pris. Alla funktioner.
              <br />
              <span className="text-brand">Ingen förhandling.</span>
            </h2>
            <p className="mt-5 text-text-muted">
              Faktureras årsvis. Alla priser inkl. 25 % moms. Byt paket när teamet växer.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TIER_CARDS.map((t) => (
              <div
                key={t.label}
                className={
                  "relative rounded-2xl p-8 border transition-all " +
                  (t.popular
                    ? "bg-sidebar text-sidebar-text border-sidebar shadow-xl md:-translate-y-2"
                    : "bg-surface border-app hover:border-brand/50")
                }
                data-testid={`landing-tier-${t.label}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-[11px] tracking-widest uppercase px-3 py-1 rounded-full">
                    Vanligast
                  </span>
                )}
                <div className={"text-[11px] tracking-widest uppercase " + (t.popular ? "text-sidebar-text/60" : "text-text-muted")}>
                  {t.label}
                </div>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="font-heading text-4xl">
                    {fmtSEK(t.monthly)}
                  </span>
                  <span className={"text-sm " + (t.popular ? "text-sidebar-text/70" : "text-text-muted")}>
                    kr/mån
                  </span>
                </div>
                <div className={"text-xs mt-1 " + (t.popular ? "text-sidebar-text/60" : "text-text-muted")}>
                  {fmtSEK(t.yearly)} kr/år · inkl. 25 % moms
                </div>
                <ul className="mt-6 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check size={14} weight="bold" className={t.popular ? "text-brand" : "text-brand"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={
                    "mt-7 block text-center py-2.5 rounded-md font-medium transition-colors " +
                    (t.popular
                      ? "bg-brand hover:bg-brand-hover text-white"
                      : "border border-app hover:bg-surface-hover")
                  }
                  data-testid={`landing-tier-cta-${t.label}`}
                >
                  Starta gratis
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-text-muted">
            Behöver du en exakt kalkyl för just ert team?{" "}
            <Link
              to="/pricing"
              className="text-brand hover:underline"
              data-testid="landing-pricing-teaser-full-link"
            >
              Öppna prisräknaren →
            </Link>
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="bg-brand text-white"
        data-testid="landing-final-cta"
      >
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight leading-tight">
            Redo att slippa Excel-torsdagar?
          </h2>
          <p className="mt-5 text-white/90 text-lg max-w-xl mx-auto">
            30 dagar gratis. Inget kort krävs. Ta ett riktigt beslut när du sett
            appen leverera på ditt eget första objekt.
          </p>
          <button
            onClick={() => navigate("/register")}
            className="mt-10 bg-white text-brand hover:bg-white/90 px-8 py-4 rounded-md font-medium inline-flex items-center gap-2 transition-colors"
            data-testid="landing-final-cta-button"
          >
            Starta gratis prövotid
            <ArrowRight size={18} weight="bold" />
          </button>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-white/80">
            <span>Vill du få den visad först?</span>
            <button
              onClick={() => setDemoOpen(true)}
              className="underline underline-offset-4 hover:text-white flex items-center gap-1.5 font-medium"
              data-testid="landing-final-cta-demo"
            >
              <VideoCamera size={16} weight="fill" />
              Boka en 20-min live demo
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-sidebar text-sidebar-text/70" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-6 py-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <div className="flex items-center gap-2 text-sidebar-text">
              <span className="h-7 w-7 rounded bg-brand flex items-center justify-center font-heading text-xs font-bold text-white">
                T
              </span>
              <span className="font-heading text-base">Tidsrapporten</span>
            </div>
            <p className="mt-3 text-xs">
              Tidrapportering, objekthantering och filarkiv för team som
              fakturerar tid mot projekt.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/pricing" className="hover:text-white transition-colors">
              Priser
            </Link>
            <Link to="/integrationer" className="hover:text-white transition-colors">
              Integrationer
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Integritetspolicy
            </Link>
            <Link to="/data-deletion" className="hover:text-white transition-colors">
              Data­radering
            </Link>
            <Link to="/login" className="hover:text-white transition-colors">
              Logga in
            </Link>
            <Link to="/register" className="hover:text-white transition-colors">
              Starta gratis
            </Link>
          </div>
        </div>
        <div className="border-t border-sidebar-text/10">
          <div className="mx-auto max-w-6xl px-6 py-5 text-xs">
            © {new Date().getFullYear()} Tidsrapporten. Alla rättigheter förbehållna.
          </div>
        </div>
      </footer>

      <DemoBookingModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
